import { BufferGeometry, Color, Float32BufferAttribute, ShapeGeometry } from 'three'
import type { Font } from 'three/addons/loaders/FontLoader.js'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
import type { PdfRect, PdfTableLayout } from 'src/domain/planTables/tableLayout'

/**
 * facetype.js (which produced `Arial_Regular.typeface.json`) scales the glyphs by
 * `100000 / (unitsPerEm * 72)` at resolution 1000, so one em of the font spans `100 / 72` of the
 * `size` passed to `Font.generateShapes`.
 */
const TYPEFACE_EM_PER_SIZE = 100 / 72

/** Few segments suffice for glyphs this small and keep big tables cheap to build. */
const GLYPH_CURVE_SEGMENTS = 4

const colorFromHex = (hex: string): Color => new Color(`#${hex.replace('#', '')}`)

const pushColor = (colors: number[], color: Color, vertexCount: number): void => {
  for (let index = 0; index < vertexCount; index++) colors.push(color.r, color.g, color.b)
}

/**
 * Cell backgrounds and grid lines of a plan table as one vertex-coloured geometry, in world
 * units relative to the table's bottom-left corner. Mirrors `make_table`: a background covers
 * its cell rect, and every grid line runs along the cell edges across the whole table with the
 * table's border width and butt ends, drawn over the backgrounds.
 */
export const buildTableFillGeometry = (
  layout: PdfTableLayout,
  ptToWorld: number,
): BufferGeometry => {
  const positions: number[] = []
  const colors: number[] = []
  const indices: number[] = []

  const pushRect = ({ xMin, yMin, xMax, yMax }: PdfRect, color: Color) => {
    const start = positions.length / 3
    positions.push(
      xMin * ptToWorld,
      yMin * ptToWorld,
      0,
      xMax * ptToWorld,
      yMin * ptToWorld,
      0,
      xMax * ptToWorld,
      yMax * ptToWorld,
      0,
      xMin * ptToWorld,
      yMax * ptToWorld,
      0,
    )
    pushColor(colors, color, 4)
    indices.push(start, start + 1, start + 2, start, start + 2, start + 3)
  }

  layout.cells.forEach(({ rect, settings }) => {
    if (settings.backgroundColorHex !== null)
      pushRect(rect, colorFromHex(settings.backgroundColorHex))
  })

  if (layout.borderWidth > 0) {
    const borderColor = colorFromHex(layout.borderColorHex)
    const halfWidth = layout.borderWidth / 2
    layout.verticalGridLineXs.forEach(x =>
      pushRect(
        { xMin: x - halfWidth, xMax: x + halfWidth, yMin: 0, yMax: layout.totalHeight },
        borderColor,
      ),
    )
    layout.horizontalGridLineYs.forEach(y =>
      pushRect(
        { xMin: 0, xMax: layout.totalWidth, yMin: y - halfWidth, yMax: y + halfWidth },
        borderColor,
      ),
    )
  }

  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3))
  geometry.setAttribute('color', new Float32BufferAttribute(colors, 3))
  geometry.setIndex(indices)
  return geometry
}

/**
 * All cell text of a plan table as one vertex-coloured geometry, in world units relative to the
 * table's bottom-left corner: every line is set in Arial at the table font size, starting at the
 * baseline position the PDF appearance stream uses. Returns null for a table without text.
 */
export const buildTableTextGeometry = (
  layout: PdfTableLayout,
  font: Font,
  ptToWorld: number,
): BufferGeometry | null => {
  const glyphSize = (layout.fontSize * ptToWorld) / TYPEFACE_EM_PER_SIZE
  const lineGeometries: BufferGeometry[] = []

  layout.cells.forEach(({ lines }) => {
    lines.forEach(({ text, x, baselineY, colorHex }) => {
      const shapes = font.generateShapes(text, glyphSize)
      if (!shapes.length) return
      const geometry = new ShapeGeometry(shapes, GLYPH_CURVE_SEGMENTS)
      geometry.deleteAttribute('normal')
      geometry.deleteAttribute('uv')
      geometry.translate(x * ptToWorld, baselineY * ptToWorld, 0)
      const colors: number[] = []
      pushColor(colors, colorFromHex(colorHex), geometry.getAttribute('position').count)
      geometry.setAttribute('color', new Float32BufferAttribute(colors, 3))
      lineGeometries.push(geometry)
    })
  })

  if (!lineGeometries.length) return null
  const merged = mergeGeometries(lineGeometries)
  lineGeometries.forEach(geometry => geometry.dispose())
  return merged
}
